import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { COUNTRY_CONFIGS } from '../lib/country-config'
import { PaktLockup, PaktMark } from '../components/pakt'
import type { CountryConfig } from '../lib/country-config'

/* ── Accordion ── */
function AccordionItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="text-base font-medium text-gray-900">{question}</span>
        <span className="ml-4 text-pakt-ink-3 text-xl select-none">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="pb-5 text-sm text-gray-600 leading-relaxed pr-8">{answer}</div>
      )}
    </div>
  )
}

/* ── Mono marker used as icon replacement ── */
function Tag({ label }: { label: string }) {
  return (
    <div className="font-pakt-mono text-[10px] tracking-[0.2em] text-pakt-orange uppercase mb-4 select-none">
      {label}
    </div>
  )
}

/* ── Step number circle ── */
function StepNum({ n, dark }: { n: number; dark?: boolean }) {
  return (
    <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center font-pakt-mono text-sm font-medium border ${dark ? 'border-pakt-ink text-pakt-ink' : 'border-pakt-orange text-pakt-orange'}`}>
      {String(n).padStart(2, '0')}
    </span>
  )
}

/* ── Landing Page ── */
interface PlatformInfo {
  companyLegalName: string | null
  companyCnpj: string | null
  privacyEmail: string | null
}

function fmtCnpj(raw: string): string {
  const d = raw.replace(/\D/g, '').padStart(14, '0').slice(-14)
  return d.length === 14
    ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`
    : raw
}

export default function Landing() {
  const { t } = useTranslation()
  const [selectedCountry, setSelectedCountry] = useState('BR')
  const countryInfo: CountryConfig | undefined = COUNTRY_CONFIGS[selectedCountry]

  // Razão social + CNPJ pro rodapé (LGPD Art. 9). Busca lazy — se falhar,
  // mantém o copyright genérico.
  const [platform, setPlatform] = useState<PlatformInfo | null>(null)
  useEffect(() => {
    fetch('/api/legal/platform')
      .then(r => r.ok ? r.json() : null)
      .then(d => setPlatform(d))
      .catch(() => null)
  }, [])

  const priceDisplay = countryInfo
    ? countryInfo.signaturePriceIsDynamic
      ? '≈ USD 1.00'
      : `${countryInfo.currency} ${countryInfo.signaturePriceLocal.toFixed(2)}`
    : ''

  const paymentMethodLabel = countryInfo
    ? countryInfo.payment.primary.replace(/_/g, ' ').toUpperCase()
    : ''

  return (
    <div className="min-h-screen bg-white">

      {/* ════════ Nav ════════ */}
      <nav className="border-b border-pakt-line bg-pakt-cream/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <PaktLockup size={22} />
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-pakt-ink-2 hover:text-pakt-ink transition"
            >
              {t('landing.cta_login', 'Login')}
            </Link>
            <Link
              to="/register"
              className="px-4 py-2 text-sm font-medium text-white bg-pakt-ink hover:bg-pakt-ink-2 transition"
            >
              {t('common.register', 'Register')}
            </Link>
          </div>
        </div>
      </nav>

      {/* ════════ Hero ════════ */}
      <section className="relative overflow-hidden bg-pakt-ink text-white">
        {/* Background mark */}
        <div className="absolute inset-0 flex items-center justify-end pr-8 pointer-events-none select-none">
          <PaktMark variant="dark" size={520} rayOpacity={0.12} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-36">
          <div className="max-w-3xl">
            <div className="font-pakt-mono text-[10px] tracking-[0.22em] text-pakt-orange uppercase mb-8 flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-pakt-orange inline-block" />
              {t('landing.hero_tag', 'Bank-verified · 30+ countries · Pay per signature')}
            </div>
            <h1 className="font-pakt-display text-5xl sm:text-6xl lg:text-7xl font-normal leading-[0.96] tracking-tight mb-8">
              {t('landing.hero', 'The only e-signature that proves who actually signed')}
            </h1>
            <p className="text-lg text-white/70 max-w-xl mb-10 leading-relaxed">
              {countryInfo
                ? t('landing.heroSub_dynamic', `Your signer pays {{price}} via {{method}} — their bank confirms their identity so you don't have to.`)
                    .replace('{{price}}', priceDisplay)
                    .replace('{{method}}', paymentMethodLabel)
                : t('landing.subtitle', 'Sign documents securely across 30+ countries with local payment methods, legal compliance, and identity verification.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/register"
                className="px-8 py-4 bg-pakt-orange text-white font-semibold hover:bg-pakt-orange-dk transition text-base text-center"
              >
                {t('landing.cta_primary', 'Send my first document — free')}
              </Link>
              <a
                href="#how-it-works"
                className="px-8 py-4 border border-white/20 text-white font-medium hover:bg-white/5 transition text-base text-center"
              >
                {t('landing.cta_secondary', 'See how it works')}
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-pakt-mono text-[10px] tracking-[0.18em] text-white/40 uppercase">
              <span>{t('landing.trust1', 'No credit card')}</span>
              <span>·</span>
              <span>{t('landing.trust2', 'First document free')}</span>
              <span>·</span>
              <span>{t('landing.trust3', 'Full legal validity')}</span>
              <span>·</span>
              <span>{t('landing.trust4', '2 minutes to start')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ Problem ════════ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <Tag label={t('landing.section_problem', '§01 · The problem')} />
            <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink mb-4">
              {t('landing.problem_title', 'Regular e-signatures have a serious problem')}
            </h2>
            <p className="text-gray-500">
              {t('landing.problem_subtitle', 'Most electronic signature tools let anyone type a name and click "sign." That is not real identity verification.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border border-pakt-line">
            {[
              { tag: '[EM]', title: t('landing.problem1_title', "Email doesn't prove identity"), desc: t('landing.problem1_desc', 'Anyone can create a throwaway email. An email link alone proves nothing about who actually clicked "sign."') },
              { tag: '[LG]', title: t('landing.problem2_title', 'Disputes have weak proof'), desc: t('landing.problem2_desc', 'When someone claims "I didn\'t sign that," most e-signature logs only show an IP address and a timestamp. Good luck in court.') },
              { tag: '[$$]', title: t('landing.problem3_title', 'Enterprise solutions are expensive'), desc: t('landing.problem3_desc', 'Real identity verification costs $5–15 per signature and requires monthly subscriptions.') },
            ].map((card, i) => (
              <div key={i} className={`p-8 ${i < 2 ? 'border-r border-pakt-line' : ''}`}>
                <div className="font-pakt-mono text-[11px] tracking-[0.2em] text-pakt-orange uppercase mb-6">{card.tag}</div>
                <h3 className="text-lg font-semibold text-pakt-ink mb-3">{card.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-0 bg-pakt-surface border border-pakt-line border-t-0 p-8 text-center">
            <h3 className="text-xl font-pakt-display font-normal text-pakt-ink mb-2">
              {t('landing.problem_solution', 'PAKT solves all three.')}
            </h3>
            <p className="text-gray-500 max-w-xl mx-auto text-sm">
              {t('landing.problem_solution_desc', "By using your signer's own bank as the identity verifier, PAKT provides irrefutable proof of identity at a fraction of the cost — with no subscription.")}
            </p>
          </div>
        </div>
      </section>

      {/* ════════ How It Works ════════ */}
      <section id="how-it-works" className="py-20 bg-pakt-surface border-t border-pakt-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <Tag label={t('landing.section_how', '§02 · How it works')} />
            <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink mb-4">
              {t('landing.how_title', 'How it works')}
            </h2>
            <p className="text-gray-500">
              {t('landing.how_subtitle', 'Two sides, one seamless process. Done in under 5 minutes.')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-pakt-line border border-pakt-line">
            {/* Sender */}
            <div className="bg-white p-8">
              <div className="font-pakt-mono text-[10px] tracking-[0.2em] text-pakt-ink-3 uppercase mb-6 border-b border-pakt-line pb-4">
                {t('landing.how_you', 'You do')}
              </div>
              <ol className="space-y-6">
                {[
                  { label: t('landing.how_s1', 'Upload your PDF'), sub: t('landing.how_s1d', 'Any contract, agreement, or document.') },
                  { label: t('landing.how_s2', 'Add signers'), sub: t('landing.how_s2d', "Enter their name and email. That's it.") },
                  { label: t('landing.how_s3', 'Click send'), sub: t('landing.how_s3d', 'Your signer gets a link. You get notified when they sign.') },
                ].map((step, i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <StepNum n={i + 1} />
                    <div>
                      <p className="font-medium text-pakt-ink">{step.label}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{step.sub}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-8 font-pakt-mono text-[10px] tracking-[0.15em] text-pakt-ink-3 uppercase">
                {t('landing.how_time_sender', 'About 2 minutes')}
              </div>
            </div>

            {/* Signer */}
            <div className="bg-white p-8">
              <div className="font-pakt-mono text-[10px] tracking-[0.2em] text-pakt-ink-3 uppercase mb-6 border-b border-pakt-line pb-4">
                {t('landing.how_signer', 'Your signer does')}
              </div>
              <ol className="space-y-6">
                {[
                  { label: t('landing.how_r1', 'Receives a link'), sub: t('landing.how_r1d', 'Via email. Works on any device, no app needed.') },
                  { label: t('landing.how_r2', 'Reads the document'), sub: t('landing.how_r2d', 'Full document preview before any commitment.') },
                  { label: t('landing.how_r3', 'Takes a selfie & pays via local method'), sub: countryInfo
                      ? t('landing.how_r3d_dynamic', 'A small {{price}} payment via {{method}} confirms their real identity.').replace('{{price}}', priceDisplay).replace('{{method}}', paymentMethodLabel)
                      : t('landing.how_r3d', 'A small payment via their local bank confirms their real identity.') },
                  { label: t('landing.how_r4', 'Signs the document'), sub: t('landing.how_r4d', 'Identity verified, legally binding, done.') },
                ].map((step, i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <StepNum n={i + 1} dark />
                    <div>
                      <p className="font-medium text-pakt-ink">{step.label}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{step.sub}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-8 font-pakt-mono text-[10px] tracking-[0.15em] text-pakt-ink-3 uppercase">
                {t('landing.how_time_signer', 'About 3 minutes')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ Country Selector ════════ */}
      <section className="py-20 bg-white border-t border-pakt-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <Tag label={t('landing.section_coverage', '§03 · Coverage')} />
            <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink mb-4">
              {t('countries.title', 'Available in your country')}
            </h2>
            <p className="text-gray-500 text-sm">
              {t('countries.subtitle', 'Select your country to see local payment options, pricing, and legal compliance info.')}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {Object.values(COUNTRY_CONFIGS)
              .filter(c => c.launchStatus !== 'restricted')
              .sort((a, b) => a.nameEn.localeCompare(b.nameEn))
              .map(c => (
                <button
                  key={c.code}
                  onClick={() => setSelectedCountry(c.code)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm border transition font-pakt-mono tracking-wide ${
                    selectedCountry === c.code
                      ? 'bg-pakt-ink text-white border-pakt-ink'
                      : 'bg-white text-pakt-ink-2 border-pakt-line hover:border-pakt-ink'
                  }`}
                >
                  <span className="text-base">{c.flag}</span>
                  <span className="hidden sm:inline text-[11px] tracking-[0.1em] uppercase">{c.nameLocal}</span>
                </button>
              ))}
          </div>

          {countryInfo && (
            <div className="max-w-2xl mx-auto border border-pakt-line overflow-hidden">
              <div className="bg-pakt-ink px-6 py-5 text-white flex items-center gap-4">
                <span className="text-3xl">{countryInfo.flag}</span>
                <div>
                  <h3 className="font-pakt-display text-xl font-normal">{countryInfo.nameLocal}</h3>
                  <p className="font-pakt-mono text-[10px] tracking-[0.15em] text-white/50 uppercase mt-0.5">{countryInfo.lawName}</p>
                </div>
              </div>
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-pakt-line">
                <div className="bg-white p-4">
                  <div className="font-pakt-mono text-[10px] tracking-[0.15em] text-pakt-ink-3 uppercase mb-1">{t('landing.pricing', 'Price per signature')}</div>
                  <div className="text-2xl font-pakt-display font-normal text-pakt-ink">{priceDisplay}</div>
                </div>
                <div className="bg-white p-4">
                  <div className="font-pakt-mono text-[10px] tracking-[0.15em] text-pakt-ink-3 uppercase mb-1">{t('sign.payment.method', 'Payment method')}</div>
                  <div className="text-lg font-pakt-mono font-normal text-pakt-ink tracking-wide">{paymentMethodLabel}</div>
                </div>
                <div className="bg-white p-4 sm:col-span-2">
                  <div className="font-pakt-mono text-[10px] tracking-[0.15em] text-pakt-ink-3 uppercase mb-1">{t('sign.view.legalBasis', 'Legal basis')}</div>
                  <div className="text-sm text-pakt-ink">{countryInfo.lawName}</div>
                </div>
              </div>
              {countryInfo.warningText && (
                <div className="px-6 pb-5">
                  <div className={`text-xs p-3 border ${
                    countryInfo.warningLevel === 'high' ? 'border-red-200 text-red-700 bg-red-50' :
                    countryInfo.warningLevel === 'medium' ? 'border-amber-200 text-amber-700 bg-amber-50' : 'border-blue-100 text-blue-700 bg-blue-50'
                  }`}>
                    {t(`landing.warning_${countryInfo.code}`, countryInfo.warningText)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ════════ Why Bank Payment ════════ */}
      <section className="py-20 bg-pakt-surface border-t border-pakt-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <Tag label={t('landing.section_mechanism', '§04 · The mechanism')} />
            <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink mb-4">
              {t('landing.bank_title', 'Why a bank payment proves identity')}
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              {t('landing.bank_subtitle', "This is the key insight behind PAKT. Your signer's bank does the identity verification for you.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border border-pakt-line">
            {[
              { tag: '[KYC]', title: t('landing.bank1_title', 'The bank already verified them'), desc: t('landing.bank1_desc', 'Every bank account requires KYC. ID documents, facial verification, address checks — the bank already did all of it.') },
              { tag: '[TXN]', title: t('landing.bank2_title', 'Payment returns verified data'), desc: t('landing.bank2_desc', "The payment confirmation returns the account holder's full legal name, tax ID, and bank details — verified by the financial institution itself.") },
              { tag: '[LAW]', title: t('landing.bank3_title', 'Accepted in court'), desc: t('landing.bank3_desc', 'A bank-verified transaction is a financial record. Combined with selfie, IP logs, and document hash, it creates an evidence package that holds up in legal proceedings.') },
            ].map((card, i) => (
              <div key={i} className={`bg-white p-8 ${i < 2 ? 'border-r border-pakt-line' : ''}`}>
                <div className="font-pakt-mono text-[11px] tracking-[0.2em] text-pakt-orange uppercase mb-6">{card.tag}</div>
                <h3 className="text-lg font-semibold text-pakt-ink mb-3">{card.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ Features ════════ */}
      <section id="features" className="py-20 bg-white border-t border-pakt-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <Tag label={t('landing.section_features', '§05 · Features')} />
            <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink mb-4">
              {t('landing.features_title', "Everything you need, nothing you don't")}
            </h2>
            <p className="text-gray-500 text-sm">
              {t('landing.features_subtitle', 'Built for professionals who need signatures that actually hold up.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-pakt-line border border-pakt-line">
            {[
              { tag: '[GL]', title: t('landing.feat1_title', 'Global — 30+ countries'), desc: t('landing.feat1_desc', 'PIX in Brazil, UPI in India, Swish in Sweden, SEPA in Europe, and more. Each country uses its own local payment rail.') },
              { tag: '[LW]', title: t('landing.feat2_title', 'Legal validity'), desc: t('landing.feat2_desc', 'Compliant with local e-signature laws in every supported country. Each signature includes the applicable legal framework.') },
              { tag: '[SC]', title: t('landing.feat3_title', 'Secure & auditable'), desc: t('landing.feat3_desc', 'SHA-256 document hash, IP logging, selfie capture, bank-verified identity, and timestamped audit trail for every signature.') },
              { tag: '[MB]', title: t('landing.feat4_title', 'Mobile-friendly'), desc: t('landing.feat4_desc', 'Signers receive a link and complete everything on their phone. No app download, no desktop required.') },
              { tag: '[PP]', title: t('landing.feat5_title', 'No subscription'), desc: t('landing.feat5_desc', 'Pay per signature, not per month. No contracts, no minimum commitments, no surprise invoices.') },
              { tag: '[FA]', title: t('landing.feat6_title', 'Ready in 2 minutes'), desc: t('landing.feat6_desc', 'Create an account, upload a PDF, add signers, and send. No setup wizards, no onboarding calls.') },
            ].map((f, i) => (
              <div key={i} className="bg-white p-8">
                <div className="font-pakt-mono text-[10px] tracking-[0.2em] text-pakt-orange uppercase mb-4">{f.tag}</div>
                <h3 className="font-semibold text-pakt-ink mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ Pricing ════════ */}
      <section className="py-20 bg-pakt-surface border-t border-pakt-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <Tag label={t('landing.section_pricing', '§06 · Pricing')} />
            <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink mb-4">
              {t('landing.pricing_title', 'Simple, transparent pricing')}
            </h2>
            <p className="text-gray-500 text-sm">
              {t('landing.pricing_subtitle', 'No monthly fees. No per-user charges. Just pay when someone signs.')}
            </p>
          </div>

          <div className="max-w-lg mx-auto border border-pakt-line overflow-hidden">
            <div className="bg-pakt-ink px-8 py-6 text-center text-white">
              <p className="font-pakt-mono text-[10px] tracking-[0.2em] text-white/50 uppercase mb-3">
                {t('landing.pricing_per_sig', 'Per signature')}
              </p>
              <div className="font-pakt-display text-5xl font-normal text-white">{priceDisplay}</div>
              {countryInfo && (
                <p className="font-pakt-mono text-[10px] tracking-[0.15em] text-white/40 uppercase mt-3">
                  {t('landing.pricing_country_note', 'Price for {{country}}').replace('{{country}}', countryInfo.nameEn)}
                  {' · '}
                  {paymentMethodLabel}
                </p>
              )}
            </div>
            <div className="px-8 py-8 bg-white">
              <ul className="space-y-4">
                {[
                  t('landing.price_inc1', 'Free account — no credit card to sign up'),
                  t('landing.price_inc2', 'Signer pays the fee, or sender covers it (+20%)'),
                  t('landing.price_inc3', 'No monthly fee, no contract'),
                  t('landing.price_inc4', 'Unlimited documents'),
                  t('landing.price_inc5', 'First document completely free'),
                  t('landing.price_inc6', 'Bank-verified identity + selfie + audit trail'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="font-pakt-mono text-pakt-orange text-sm mt-0.5">+</span>
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="mt-8 block w-full text-center px-6 py-3 bg-pakt-orange text-white font-semibold hover:bg-pakt-orange-dk transition"
              >
                {t('landing.pricing_cta', 'Start for free')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ Comparison ════════ */}
      <section className="py-20 bg-white border-t border-pakt-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink text-center mb-12">
            {t('landing.compare_title', 'PAKT vs. simple e-signature tools')}
          </h2>

          <div className="max-w-3xl mx-auto overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-pakt-ink">
                  <th className="py-3 pr-4 text-sm font-pakt-mono text-pakt-ink-3 uppercase tracking-widest">{t('landing.compare_feature', 'Feature')}</th>
                  <th className="py-3 px-4 text-sm font-pakt-mono text-pakt-orange uppercase tracking-widest text-center">PAKT</th>
                  <th className="py-3 pl-4 text-sm font-pakt-mono text-pakt-ink-3 uppercase tracking-widest text-center">{t('landing.compare_others', 'Simple e-sig')}</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  [t('landing.cmp1', 'Verified signer identity'), `+ ${t('landing.cmp1_pakt', 'Bank-verified')}`, `− ${t('landing.cmp1_other', 'Email only')}`],
                  [t('landing.cmp2', 'Irrefutable proof'), `+ ${t('landing.cmp2_pakt', 'Financial record')}`, `− ${t('landing.cmp2_other', 'IP + timestamp')}`],
                  [t('landing.cmp3', 'No subscription'), '+', `− ${t('landing.cmp3_other', '$20-50/mo')}`],
                  [t('landing.cmp4', 'Mobile-first signing'), '+', `~ ${t('landing.cmp4_other', 'Varies')}`],
                  [t('landing.cmp5', 'Complete audit trail'), `+ ${t('landing.cmp5_pakt', 'Bank + selfie + hash')}`, `~ ${t('landing.cmp5_other', 'Basic log')}`],
                  [t('landing.cmp6', 'Legal validity'), `+ ${t('landing.cmp6_pakt', '30+ countries')}`, `~ ${t('landing.cmp6_other', 'Limited')}`],
                ].map(([feat, pakt, other], i, arr) => (
                  <tr key={i} className={i < arr.length - 1 ? 'border-b border-pakt-line' : ''}>
                    <td className="py-4 pr-4 text-gray-700">{feat}</td>
                    <td className="py-4 px-4 text-center font-pakt-mono text-[12px] text-pakt-success font-medium">{pakt}</td>
                    <td className="py-4 pl-4 text-center font-pakt-mono text-[12px] text-pakt-ink-3">{other}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ════════ Use Cases ════════ */}
      <section className="py-20 bg-pakt-surface border-t border-pakt-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <Tag label={t('landing.section_usecases', "§07 · Who it's for")} />
            <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink mb-4">
              {t('landing.usecases_title', "Built for professionals who can't afford doubt")}
            </h2>
            <p className="text-gray-500 text-sm">
              {t('landing.usecases_subtitle', 'When a signature is challenged, PAKT gives you the proof that holds up.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-pakt-line border border-pakt-line">
            {[
              { tag: '[JD]', title: t('landing.uc1_title', 'Lawyers'), desc: t('landing.uc1_desc', 'Client retainers, NDAs, settlement agreements with verified counterparties.') },
              { tag: '[RE]', title: t('landing.uc2_title', 'Real estate agents'), desc: t('landing.uc2_desc', 'Rental agreements, purchase offers, and mandates signed by verified tenants and buyers.') },
              { tag: '[HR]', title: t('landing.uc3_title', 'HR teams'), desc: t('landing.uc3_desc', 'Employment contracts, onboarding documents, and policy acknowledgments with identity proof.') },
              { tag: '[CF]', title: t('landing.uc4_title', 'Accountants'), desc: t('landing.uc4_desc', 'Engagement letters, tax authorizations, and financial statements with client verification.') },
              { tag: '[VC]', title: t('landing.uc5_title', 'Startups'), desc: t('landing.uc5_desc', 'SAFEs, contractor agreements, and investor docs — no enterprise pricing needed.') },
            ].map((uc, i) => (
              <div key={i} className="bg-white p-6">
                <div className="font-pakt-mono text-[10px] tracking-[0.2em] text-pakt-orange uppercase mb-4">{uc.tag}</div>
                <h3 className="font-semibold text-pakt-ink mb-2">{uc.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ Testimonials ════════ */}
      <section className="py-20 bg-white border-t border-pakt-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink text-center mb-12">
            {t('landing.testimonials_title', 'What professionals are saying')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-pakt-line border border-pakt-line">
            {[
              { initials: 'MR', name: t('landing.test1_name', 'Maria R.'), role: t('landing.test1_role', 'Real Estate Agency Owner'), quote: t('landing.test1_quote', '"We switched from DocuSign to PAKT for our rental contracts. The bank verification gives us proof that the actual tenant signed — not just someone with their email."') },
              { initials: 'CF', name: t('landing.test2_name', 'Carlos F.'), role: t('landing.test2_role', 'Corporate Lawyer'), quote: t('landing.test2_quote', '"The pay-per-signature model is perfect for my solo practice. I only send 10-15 contracts a month — why would I pay $50/month for that?"') },
              { initials: 'AK', name: t('landing.test3_name', 'Ana K.'), role: t('landing.test3_role', 'Head of People, Series A Startup'), quote: t('landing.test3_quote', '"We onboard remote employees in 5 countries. PAKT handles the local payment method in each one. Our HR team doesn\'t need to think about it."') },
            ].map((t_, i) => (
              <div key={i} data-placeholder="true" className="bg-white p-8 relative">
                <div className="absolute top-4 right-4 font-pakt-mono text-[9px] tracking-[0.15em] text-pakt-ink-3 uppercase border border-pakt-line px-2 py-0.5">
                  {t('landing.testimonial_placeholder', 'Example')}
                </div>
                <p className="text-gray-600 text-sm mb-6 italic leading-relaxed">{t_.quote}</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-pakt-surface border border-pakt-line flex items-center justify-center font-pakt-mono text-xs text-pakt-ink-2">
                    {t_.initials}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-pakt-ink">{t_.name}</p>
                    <p className="text-xs text-gray-400">{t_.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ FAQ ════════ */}
      <section className="py-20 bg-pakt-surface border-t border-pakt-line">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Tag label={t('landing.section_faq', '§08 · FAQ')} />
            <h2 className="text-3xl sm:text-4xl font-pakt-display font-normal text-pakt-ink">
              {t('landing.faq_title', 'Frequently asked questions')}
            </h2>
          </div>

          <div className="bg-white border border-pakt-line px-6">
            <AccordionItem question={t('landing.faq1_q', 'Where does the payment money go?')} answer={t('landing.faq1_a', 'The signature fee goes to PAKT to cover the cost of identity verification, document storage, and the audit trail. It is not a payment between signer and sender — it is the cost of the verification service itself.')} />
            <AccordionItem question={t('landing.faq2_q', "What if the signer doesn't want to pay?")} answer={t('landing.faq2_a', 'The sender can choose to cover the signature fee instead (with a 20% surcharge). In that case, the signer completes the selfie and identity steps without any payment. You can configure this per document.')} />
            <AccordionItem question={t('landing.faq3_q', 'Is this legally valid in my country?')} answer={t('landing.faq3_a', 'PAKT is designed to comply with e-signature laws in 30+ countries. Each country page shows the specific legal framework (e.g., ICP-Brasil for Brazil, eIDAS for Europe, ESIGN Act for the US). The bank-verified identity adds a layer of proof that exceeds what most simple e-signature tools provide.')} />
            <AccordionItem question={t('landing.faq4_q', 'What happens if the payment expires or fails?')} answer={t('landing.faq4_a', 'Payment codes (like PIX QR codes) have a 30-minute expiration. If it expires, the signer simply generates a new one. The document remains available for signing until the sender-defined deadline.')} />
            <AccordionItem question={t('landing.faq5_q', 'Can I use PAKT for international documents?')} answer={t('landing.faq5_a', "Yes. Each signer uses their own country's local payment method. A Brazilian signer uses PIX, an Indian signer uses UPI, a German signer uses SEPA — all on the same document. The sender just adds signers and PAKT handles the rest.")} />
            <AccordionItem question={t('landing.faq6_q', 'Does the signer need a PAKT account?')} answer={t('landing.faq6_a', 'No. Signers receive a link and complete the entire process (read document, selfie, payment, signature) without creating an account. Only the sender needs an account.')} />
            <AccordionItem question={t('landing.faq7_q', 'How can someone verify a signed document is authentic?')} answer={t('landing.faq7_a', 'Every signed document includes a unique verification code and QR code. Anyone can visit the PAKT verification page, enter the code, and see the full audit trail — including who signed, when, and the identity verification details.')} />
            <AccordionItem question={t('landing.faq8_q', 'How long are signed documents stored?')} answer={t('landing.faq8_a', 'Signed documents and their audit trails are stored securely for a minimum of 5 years. Senders can also download the complete signed PDF with embedded audit trail at any time from their dashboard.')} />
          </div>
        </div>
      </section>

      {/* ════════ Final CTA ════════ */}
      <section className="bg-pakt-orange py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-pakt-display text-4xl sm:text-5xl font-normal text-white mb-4 leading-tight">
            {t('landing.final_cta_title', 'Send your first contract today')}
          </h2>
          <p className="text-white/80 mb-10 text-lg max-w-xl mx-auto">
            {t('landing.final_cta_desc', '30 second signup. No credit card. First document on us.')}
          </p>
          <Link
            to="/register"
            className="inline-block px-10 py-4 bg-pakt-ink text-white font-semibold hover:bg-pakt-ink-2 transition text-base"
          >
            {t('landing.final_cta_button', 'Create free account')}
          </Link>
        </div>
      </section>

      {/* ════════ Footer ════════ */}
      <footer className="bg-pakt-ink text-white/40 py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="mb-4">
                <PaktLockup size={20} variant="paper" color="var(--pakt-cream)" />
              </div>
              <p className="text-sm leading-relaxed">
                {t('footer.tagline', 'Electronic signatures for the world. Secure, legal, and easy to use.')}
              </p>
            </div>
            <div>
              <h4 className="font-pakt-mono text-[10px] tracking-[0.2em] text-white/60 uppercase mb-4">
                {t('footer.product', 'Product')}
              </h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition">{t('footer.features', 'Features')}</a></li>
                <li><Link to="/pricing" className="hover:text-white transition">{t('footer.pricing', 'Pricing')}</Link></li>
                <li><Link to="/verify" className="hover:text-white transition">{t('verify.title', 'Verify Document')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-pakt-mono text-[10px] tracking-[0.2em] text-white/60 uppercase mb-4">
                {t('footer.support', 'Support')}
              </h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/help" className="hover:text-white transition">{t('footer.help', 'Help center')}</Link></li>
                <li><Link to="/status" className="hover:text-white transition">{t('footer.status', 'Status')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-pakt-mono text-[10px] tracking-[0.2em] text-white/60 uppercase mb-4">
                {t('footer.legal', 'Legal')}
              </h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition">{t('footer.privacy', 'Privacy Policy')}</Link></li>
                <li><Link to="/terms" className="hover:text-white transition">{t('footer.terms', 'Terms of Service')}</Link></li>
                <li><Link to="/dpa" className="hover:text-white transition">{t('footer.dpa', 'DPA')}</Link></li>
                <li><Link to="/subprocessors" className="hover:text-white transition">{t('footer.subprocessors', 'Sub-processors')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/5 pt-8 font-pakt-mono text-[10px] tracking-[0.15em] text-white/25 uppercase text-center space-y-2">
            <div>
              {t('footer.copyright', '© {{year}} {{company}}. All rights reserved.', {
                year: new Date().getFullYear(),
                company: platform?.companyLegalName || 'Pakt Technologies',
              })}
              {platform?.companyCnpj && (
                <span> · CNPJ {fmtCnpj(platform.companyCnpj)}</span>
              )}
            </div>
            {platform?.privacyEmail && (
              <div className="normal-case tracking-normal text-white/20">
                Canal de privacidade (LGPD):{' '}
                <a href={`mailto:${platform.privacyEmail}`} className="hover:text-white/40 underline">
                  {platform.privacyEmail}
                </a>
                {' · '}
                <Link to="/privacidade/exercer-direitos" className="hover:text-white/40 underline">
                  exercer direitos
                </Link>
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  )
}
